import { X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useDeleteSkill } from '@/hooks/use-profile';
import type { ProfileSkill } from '@/hooks/use-profile';

interface SkillsSectionProps {
  skills: ProfileSkill[];
}

const categoryLabels: Record<string, string> = {
  skill: 'Skills',
  methodology: 'Methodologies',
  leadership: 'Leadership',
  industry: 'Industries',
  tool: 'Tools',
  client_level: 'Client Levels',
  project_type: 'Project Types',
  certification: 'Certifications'
};

const categoryColors: Record<string, string> = {
  skill: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400',
  methodology: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  leadership: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400',
  industry: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  tool: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  client_level: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  project_type: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  certification: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
};

export function SkillsSection({ skills }: SkillsSectionProps) {
  const deleteSkill = useDeleteSkill();

  // Group skills by category
  const grouped = skills.reduce<Record<string, ProfileSkill[]>>((acc, skill) => {
    const cat = skill.category || 'skill';
    return { ...acc, [cat]: [...(acc[cat] ?? []), skill] };
  }, {});

  if (skills.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Skills</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No skills yet. Upload a CV to extract your skills automatically.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Skills ({skills.length})</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(grouped).map(([category, catSkills]) => (
          <div key={category}>
            <h4 className="mb-2 text-xs font-semibold text-muted-foreground uppercase">
              {categoryLabels[category] ?? category} ({catSkills.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {catSkills.map((skill) => (
                <span
                  key={skill.id}
                  className={`group inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    categoryColors[category] ??
                    'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}
                >
                  {skill.skill}
                  <button
                    onClick={() => deleteSkill.mutate(skill.id)}
                    className="ml-0.5 hidden rounded-full p-0.5 hover:bg-black/10 group-hover:inline-flex"
                    title="Remove skill"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
